<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import { usersApi } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import Layout from "../../components/layout/Layout.svelte";

  let { user = null, initialQuery = "" }: {
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
    initialQuery?: string;
  } = $props();

  let query = $state("");
  let results: Array<{ id: string; name: string; nickname: string | null; avatar_url: string | null }> = $state([]);
  let searching = $state(false);
  let searched = $state(false);
  let searchTimeout: ReturnType<typeof setTimeout>;
  let queryHydrated = false;

  $effect(() => {
    if (queryHydrated) return;
    query = initialQuery;
    queryHydrated = true;
  });

  function handleInput() {
    clearTimeout(searchTimeout);
    searched = false;
    if (query.trim().length < 2) {
      results = [];
      return;
    }
    searchTimeout = setTimeout(doSearch, 300);
  }

  async function doSearch() {
    searching = true;
    try {
      const res = await usersApi.search(query);
      if (res.success) {
        results = res.data || [];
        searched = true;
      }
    } catch {
      addNotification("error", "검색에 실패했습니다.");
    } finally {
      searching = false;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") {
      clearTimeout(searchTimeout);
      if (query.trim().length >= 2) doSearch();
    }
  }

  function goToProfile(userId: string) {
    router.visit(`/users/${userId}`);
  }

  let isEmpty = $derived(searched && results.length === 0);
</script>

<Layout {user}>
  <div class="max-w-2xl mx-auto px-4 py-8">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold" style="color: var(--text-primary);">사용자 검색</h1>
      <p class="text-sm mt-1" style="color: var(--text-muted);">이름 또는 닉네임으로 검색하세요</p>
    </div>

    <!-- Search Input -->
    <div class="relative mb-6">
      <div class="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
        {#if searching}
          <svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24" style="color: var(--accent);">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        {:else}
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        {/if}
      </div>
      <input
        type="text"
        bind:value={query}
        oninput={handleInput}
        onkeydown={handleKeydown}
        placeholder="이름 또는 닉네임 (2자 이상)"
        class="w-full pl-12 pr-10 py-4 rounded-2xl text-sm outline-none transition-all"
        style="background: var(--surface); color: var(--text-primary); border: 1px solid var(--border);"
      />
      {#if query}
        <button
          onclick={() => { query = ""; results = []; searched = false; }}
          aria-label="검색어 지우기"
          class="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors"
          style="color: var(--text-muted);"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      {/if}
    </div>

    <!-- Results -->
    {#if results.length > 0}
      <div class="space-y-2">
        <p class="text-xs font-medium px-1 mb-3" style="color: var(--text-muted);">
          검색 결과 {results.length}명
        </p>
        {#each results as result}
          <button
            onclick={() => goToProfile(result.id)}
            class="w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-all group"
            style="background: var(--surface); border: 1px solid var(--border);"
            onmouseenter={(e) => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(32,178,170,0.3)'}
            onmouseleave={(e) => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'}
          >
            {#if result.avatar_url}
              <img src={result.avatar_url} alt={result.name} class="w-12 h-12 rounded-full object-cover flex-shrink-0" />
            {:else}
              <div
                class="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0"
                style="background: var(--accent-dim); color: var(--accent);"
              >
                {result.name?.charAt(0) ?? "?"}
              </div>
            {/if}
            <div class="flex-1 min-w-0">
              <p class="font-semibold truncate" style="color: var(--text-primary);">{result.name}</p>
              {#if result.nickname}
                <p class="text-sm truncate" style="color: var(--text-muted);">@{result.nickname}</p>
              {/if}
            </div>
            <svg class="w-5 h-5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--accent);">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        {/each}
      </div>
    {:else if isEmpty}
      <!-- No results -->
      <div class="text-center py-20">
        <div class="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style="background: var(--surface);">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <p class="font-medium" style="color: var(--text-secondary);">검색 결과가 없습니다</p>
        <p class="text-sm mt-1" style="color: var(--text-muted);">다른 이름이나 닉네임으로 검색해보세요</p>
      </div>
    {:else if !query || query.length < 2}
      <!-- Initial state -->
      <div class="text-center py-20">
        <div class="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style="background: var(--surface);">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <p class="font-medium" style="color: var(--text-secondary);">여행 친구를 찾아보세요</p>
        <p class="text-sm mt-1" style="color: var(--text-muted);">2자 이상 입력하면 검색이 시작됩니다</p>
      </div>
    {/if}
  </div>
</Layout>
